import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useMsal } from "@azure/msal-react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Dialog,
  IconButton,
  Radio,
  RadioGroup,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useMutation, useQuery } from "@tanstack/react-query";


import { BackIcon, BlackCaretDownIcon } from "../../assets/svg";
import {
  EmptyState,
  Loader,
  PyButton,
  TOASTER_TYPE,
  ToastMessage,
} from "../../components";
import {
  getSessionData,
  msalConfig,
  setUpHeaders,
  tokenRequest,
  urlConfig,
} from "../../config";
import {
  createStripeCustomer,
  createStripeSetupIntent,
  getCardToken,
  getPaymentGateway,
  retrieveStripeCustomer,
  selectDefaultCardToken,
} from "../../services/api";
import { decodeToken } from "../../utils/helper";
import { AddPayStackPaymentMethod } from "./components";
import { AddStripePaymentMethod } from "./components/AddMethod/Stripe";
import { ConfirmSelection } from "./components/ConfirmSelection";
import { images } from "../../constant";

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
);

export type TCredential = {
  consumerUniqueReferenceNumber: string;
  customerUniqueReferenceNumber: string;
  email: string;
};

export const PaymentMethod = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [searchParam] = useSearchParams();
  const getCredentials = searchParam.get("credential");
  // let credentials: TCredential | any = getCredentials ? JSON.parse(getCredentials as string) : null
  const [credentials, setCredentials] = React.useState<TCredential | any>(
    JSON.parse(getCredentials as string) || null,
  );
  const [loadingId] = React.useState<number | null>(null);
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [selectedTokenUrn, setSelectedTokenUrn] = React.useState<string>("");
  const [expanded, setExpanded] = React.useState<string | false>("panel1");
  const { data, isLoading, isFetched, refetch } = useQuery({
    queryKey: ["GetCardToken"],
    queryFn: () =>
      getCardToken({
        cusUrn: credentials?.customerUniqueReferenceNumber,
        conUrn: credentials?.consumerUniqueReferenceNumber,
        wUrn: accounts[0].idTokenClaims?.sub,
        name: accounts[0].idTokenClaims?.family_name,
      }),
    enabled: !!accounts && accounts.length >= 1 && credentials !== null,
    refetchOnWindowFocus: false,
  });

  console.log({accounts})

  const { data: gatewayData, isLoading: isLoadingGatewaySettings } = useQuery({
    queryKey: [
      "GetGatewaySettings",
      credentials?.customerUniqueReferenceNumber,
    ],
    queryFn: () =>
      getPaymentGateway({
        customerUniqueReferenceNumber:
          credentials?.customerUniqueReferenceNumber,
        consumerUniqueReferenceNumber:
          credentials?.consumerUniqueReferenceNumber,
      }),
    enabled: !!credentials?.customerUniqueReferenceNumber,
  });

  const isStripeMerchant = React.useMemo(() => {
    if (!gatewayData?.data) return false;
    const gateways = gatewayData.data;
    const stripeGateway = gateways.find(
      (g: any) => g.paymentGatewayType?.toLowerCase() === "stripe",
    );
    return !!stripeGateway;
  }, [gatewayData]);

  const isStripe: boolean =
    isStripeMerchant || credentials?.isStripeMerchant || false;

  const [stripeClientSecret, setStripeClientSecret] = React.useState<
    string | null
  >(null);
  const [stripeIntentData, setStripeIntentData] = React.useState<any>(null);
  const [stripeCustomer, setStripeCustomer] = React.useState<any>(null);
  const [isCustomerLoading, setIsCustomerLoading] =
    React.useState<boolean>(false);

  const {
    data: stripeCustomerData,
    isLoading: stripeCustomerIsLoading,
    isFetched: stripeCustomerIsFetched,
  } = useQuery({
    queryKey: ["GetStripeCustomer", credentials?.consumerUniqueReferenceNumber],
    queryFn: () =>
      retrieveStripeCustomer({
        customerUniqueReferenceNumber:
          credentials?.customerUniqueReferenceNumber,
        consumerUniqueReferenceNumber:
          credentials?.consumerUniqueReferenceNumber,
      }),
    enabled:
      isStripe &&
      !!credentials?.customerUniqueReferenceNumber &&
      !!credentials?.consumerUniqueReferenceNumber,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // Effect to handle customer creation if no existing customer found
  React.useEffect(() => {
    const handleCustomerSetup = async () => {
      if (!isStripe || !stripeCustomerIsFetched || !credentials) return;

      // If customer already exists from search, use it
      if (stripeCustomerData?.customer) {
        setStripeCustomer(stripeCustomerData.customer);
        return;
      }

      // No customer found, create a new one
      setIsCustomerLoading(true);
      try {
        const newCustomer = await createStripeCustomer({
          email: credentials.email,
          name: credentials.consumerUniqueReferenceNumber,
          metadata: {
            consumerUniqueReferenceNumber:
              credentials.consumerUniqueReferenceNumber,
            customerUniqueReferenceNumber:
              credentials.customerUniqueReferenceNumber,
          },
          customerUniqueReferenceNumber:
            credentials.customerUniqueReferenceNumber,
        });

        if (newCustomer?.id) {
          setStripeCustomer(newCustomer);
        }
      } catch (error) {
        console.error("Error creating Stripe customer:", error);
      } finally {
        setIsCustomerLoading(false);
      }
    };

    handleCustomerSetup();
  }, [stripeCustomerIsFetched, stripeCustomerData, credentials, isStripe]);

  const { data: stripeData, isLoading: isStripeLoading } = useQuery({
    queryKey: ["StripeSetupIntent", stripeCustomer?.id],
    queryFn: () =>
      createStripeSetupIntent({
        customerUniqueReferenceNumber:
          credentials?.customerUniqueReferenceNumber,
        customerEmail: credentials?.email,
        consumerUniqueReferenceNumber:
          credentials?.consumerUniqueReferenceNumber,
        customerId: stripeCustomer?.id,
      }),
    enabled:
      expanded === "panel2" &&
      isStripe &&
      !!stripeCustomer?.id &&
      !!credentials?.customerUniqueReferenceNumber,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (stripeData) {
      const clientSecret =
        stripeData?.clientSecret || stripeData?.client_secret;
      const intentId = stripeData?.setupIntentId || stripeData?.paymentIntentId;

      if (clientSecret) {
        setStripeClientSecret(clientSecret);
        setStripeIntentData({ ...stripeData, clientSecret, intentId });
      }
    }
  }, [stripeData]);

  React.useLayoutEffect(() => {
    if (!credentials) {
      const sessionData = getSessionData();
      setCredentials(sessionData);
    }
  }, [navigate, credentials]);

  React.useEffect(() => {
    if (credentials) refetch();
  }, [credentials, refetch]);

  React.useEffect(() => {
    if (!data && accounts && accounts.length >= 1) {
      refetch();
    } else {
    }
  }, [accounts, data, refetch]);

  const handleBack = () => {
    window.close();
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleToggleConfirm = () => setShowConfirm(!showConfirm);

  const cardTokens = data && data?.data?.data ? data?.data?.data : [];

  return (
    <section className="w-full min-h-[80vh] bg-white py-10 lg:py-16">
      {(isLoading || isLoadingGatewaySettings) && <Loader isFullPage />}
      <div className="container flex flex-col gap-6 h-full w-full">
        <IconButton
          onClick={handleBack}
          className="!inline-flex !w-fit !items-center !gap-2 !p-1 !rounded-lg !text-sm text-py_black-950 font-normal lg:!text-base">
          <BackIcon />
          Back
        </IconButton>

        <div className="!rounded-2xl md:border md:border-py_black-100 md:p-10 xl:p-20">
          <h3 className="text-lg text-py_black-950 font-semibold mb-6 md:text-xl lg:mb-8 lg:text-2xl">
            Payment method
          </h3>

          <section className="flex flex-col w-full gap-4">
            <Accordion
              expanded={expanded === "panel1"}
              onChange={handleChange("panel1")}
              className="border !rounded-2xl !shadow-none !px-4 !py-5 md:!border-py_black-100 md:!p-10">
              <div className="headerSection">
                <AccordionSummary
                  expandIcon={<BlackCaretDownIcon />}
                  aria-controls="panel1a-content"
                  sx={{
                    "& .MuiAccordionSummary-content, .Mui-expanded": {
                      margin: "0 !important",
                    },

                    "& .Mui-expanded": {
                      marginBottom: "2.5rem !important",

                      "@media (max-width: 576px)": {
                        marginBottom: "2rem !important",
                      },
                    },
                  }}
                  id="panel1a-header"
                  className="!px-0 !min-h-fit !my-0">
                  <div className="headerContainer">
                    <h3 className="text-base text-py_black-950 font-medium md:text-[21.6px]">
                      Choose from existing payment methods
                    </h3>
                  </div>
                </AccordionSummary>
              </div>

              <AccordionDetails className="!p-0">
                {isLoading && (
                  <div className="h-[100px] w-full flex items-center justify-center">
                    <CircularProgress />
                  </div>
                )}

                {isFetched && (
                  <>
                    {cardTokens.length < 1 ? (
                      <EmptyState
                        width="w-[150px] lg:w-[200px]"
                        height="h-[150px] lg:h-[200px]"
                        emptyText="You have no existing payment method here"
                      />
                    ) : (
                      <ul className="flex flex-col w-full overflow-y-auto max-h-[300px] md:max-h-[320px]">
                        <RadioGroup
                          className="flex flex-col w-full !gap-2"
                          name="cardToken"
                          onChange={({ target }) =>
                            setSelectedTokenUrn(target.value)
                          }>
                          {cardTokens.map((token: any, id: number) => (
                            <li
                              key={id}
                              className="!px-3 !py-2 rounded-lg border border-py_black-100 flex items-center md:!p-4">
                              <Radio
                                className="!p-0 !pr-1 md:!p-1 md:!pr-2"
                                value={token.uniqueReferenceNumber}
                              />
                              <div className="inline-flex !gap-4 items-center justify-between text-xs md:text-sm lg:w-[320px]">
                                <h4 className="text-py_black-400 font-medium leading-[19.6px]">
                                  Debit/ Credit Card
                                </h4>

                                <div className="inline-flex items-center !gap-1">
                                  {token.cardType.includes("visa") && (
                                    <img
                                      src={images.VisaLogo}
                                      alt="visa logo"
                                      className="w-[26px] h-[16px] object-cover"
                                    />
                                  )}
                                  {token.cardType.includes("verve") && (
                                    <img
                                      src={images.VerveLogo}
                                      alt="verve logo"
                                      className="w-[46px] h-[16px] object-cover"
                                    />
                                  )}
                                  {token.cardType.includes("mastercard") && (
                                    <img
                                      src={images.MasterCardLogo}
                                      alt="mastercard logo"
                                      className="w-[26px] h-[16px] object-cover"
                                    />
                                  )}
                                  <span className="text-py_black-950 font-normal leading-[16.8px]">
                                    ***{token?.lastFourDigit}
                                  </span>
                                </div>
                              </div>
                              <PyButton
                                click={handleToggleConfirm}
                                // click={() => handleSelectDefaultCard(token, id)}
                                isDisabled={
                                  loadingId === id ||
                                  selectedTokenUrn !==
                                    token.uniqueReferenceNumber
                                }
                                loading={loadingId === id}
                                variant={
                                  selectedTokenUrn ===
                                  token.uniqueReferenceNumber
                                    ? "Primary"
                                    : "SecondaryOutline"
                                }
                                extraClass="!h-[1.85rem] !text-xs !px-3 !ml-auto md:!h-[2rem] md:!px-4 md:!text-sm">
                                {selectedTokenUrn ===
                                token.uniqueReferenceNumber
                                  ? "Continue"
                                  : "Select"}
                              </PyButton>
                            </li>
                          ))}
                        </RadioGroup>
                      </ul>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel2"}
              onChange={handleChange("panel2")}
              className="border !rounded-2xl !shadow-none !px-4 !py-5 md:!border-py_black-100 md:!p-10 before:!content-none">
              <div className="headerSection">
                <AccordionSummary
                  expandIcon={<BlackCaretDownIcon />}
                  aria-controls="panel1a-content"
                  sx={{
                    "& .MuiAccordionSummary-content, .Mui-expanded": {
                      margin: "0 !important",
                    },

                    "& .Mui-expanded": {
                      marginBottom: "2.5rem !important",

                      "@media (max-width: 576px)": {
                        marginBottom: "2rem !important",
                      },
                    },
                  }}
                  id="panel1a-header"
                  className="!px-0 !min-h-fit !my-0">
                  <div className="headerContainer">
                    <h3 className="text-base text-py_black-950 font-medium md:text-[21.6px]">
                      Add payment method
                    </h3>
                  </div>
                </AccordionSummary>
              </div>

              <AccordionDetails className="!p-0">
                {isLoadingGatewaySettings ? (
                  <div className="h-[100px] w-full flex items-center justify-center">
                    <CircularProgress />
                  </div>
                ) : isStripe ? (
                  isStripeLoading ||
                  stripeCustomerIsLoading ||
                  isCustomerLoading ? (
                    <div className="flex flex-col gap-4 w-full rounded-2xl md:border md:border-py_black-100 md:p-6 lg:p-12 animate-pulse">
                      <div className="w-full h-[200px] bg-gray-200 rounded-lg"></div>
                      <div className="flex gap-4 items-center mt-4">
                        <div className="w-[104px] h-[40px] bg-gray-200 rounded"></div>
                        <div className="w-[104px] h-[40px] bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ) : !!stripeClientSecret ? (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret: stripeClientSecret,
                        appearance: {
                          theme: "stripe",
                        },
                      }}>
                      <AddStripePaymentMethod
                        cardTokenLoading={isLoading}
                        refetchCardToken={refetch}
                        openMethodAccordion={() => setExpanded("panel1")}
                        credentials={credentials as TCredential}
                        setupIntentData={stripeIntentData}
                        stripeCustomer={stripeCustomer}
                      />
                    </Elements>
                  ) : null
                ) : (
                  <AddPayStackPaymentMethod
                    cardTokenLoading={isLoading}
                    refetchCardToken={refetch}
                    openMethodAccordion={() => setExpanded("panel1")}
                    credentials={credentials as TCredential}
                    isPaystack={!isStripeMerchant}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </section>
        </div>
      </div>

      <Dialog
        sx={{
          "& .MuiPaper-root": {
            padding: "1.25rem",
            margin: "1rem",
            width: "100%",
          },
        }}
        open={showConfirm}
        onClose={handleToggleConfirm}>
        <ConfirmSelection
          handleClose={handleToggleConfirm}
          cardToken={cardTokens.find(
            (item: any) => item.uniqueReferenceNumber === selectedTokenUrn,
          )}
        />
      </Dialog>
    </section>
  );
};
